import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Package, MapPin, Phone, CheckCircle2, Loader2, Truck, Map,
  Camera, Navigation, Timer, CreditCard, Banknote, StickyNote, AlertTriangle, Clock
} from "lucide-react";
import DeliveryPhotoModal from "@/components/estafeta/DeliveryPhotoModal";
import ArrivedCountdown from "@/components/estafeta/ArrivedCountdown";

export default function OrderCard({ order, onPickup, onDeliver, onArrived, loading, token, t }) {
  const [showPhoto, setShowPhoto] = useState(false);
  const ACTIVE_PICKUP_STATUSES = new Set([
    "ready",
    "sent",
    "enviado",
    "dispatched",
    "preparing",
    "pronto",
    "awaiting_payment",
  ]);
  const isDelivering = order.status === "delivering";
  const isReady = ACTIVE_PICKUP_STATUSES.has((order.status || "").toLowerCase());
  const statusLabel = isDelivering ? t("enRoute") : isReady ? t("readyStatus") : t("deliveredStatus");
  const hasArrived = !!order.arrived_at;
  const addr = order.delivery_address || "";

  const openMaps = () => {
    if (!addr) return;
    const encoded = encodeURIComponent(addr);
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    window.open(isIOS ? `maps://maps.apple.com/?q=${encoded}` : `https://www.google.com/maps/search/?api=1&query=${encoded}`, "_blank");
  };

  const openWaze = () => {
    if (!addr) return;
    window.open(`https://waze.com/ul?q=${encodeURIComponent(addr)}&navigate=yes`, "_blank");
  };

  const minutesAgo = (dateStr) => {
    if (!dateStr) return 0;
    return Math.floor((Date.now() - new Date(dateStr).getTime()) / 60000);
  };

  const timeAgo = (dateStr) => {
    const diff = minutesAgo(dateStr);
    if (diff < 1) return t("now");
    if (diff < 60) return `${diff}min`;
    return `${Math.floor(diff / 60)}h${diff % 60}min`;
  };

  const mins = minutesAgo(order.created_at);
  const urgencyColor = mins > 45 ? "text-red-600" : mins > 25 ? "text-amber-600" : "text-gray-400";

  const dispatchMinutes = minutesAgo(order.dispatched_at) || null;
  const pickupMinutes = minutesAgo(order.picked_up_at) || null;
  const pickupLate = isReady && !!order.deliverer_id && dispatchMinutes != null && dispatchMinutes >= 10;
  const deliveryLate = isDelivering && pickupMinutes != null && pickupMinutes >= 25;
  const gpsStale = !!(order.operational_status || {}).gps_stale;
  const dispatchedTime = order.dispatched_at
    ? new Date(order.dispatched_at).toLocaleTimeString("pt-PT", { hour: "2-digit", minute: "2-digit" })
    : null;

  const paymentLabel = {
    cash: t("cash"), dinheiro: t("cash"), efectivo: t("cash"), local: t("cash"), card: t("card"), online: t("online"), mbway: "MBWay", bizum: "Bizum",
    card_restaurant: t("paymentCardOnDelivery"), restaurante_card: t("paymentCardOnDelivery"),
  };

  // Card-on-delivery: "paid" only counts when gateway confirmed OR physically delivered
  const _CARD_ON_DELIVERY = new Set(["card_restaurant", "restaurante_card", "card_delivery", "cartao_entrega"]);
  const isCardOnDelivery = _CARD_ON_DELIVERY.has(order.payment_method);
  const isCash = new Set(["cash", "dinheiro", "efectivo", "local"]).has((order.payment_method || "").toLowerCase());
  const isPaid = !isCash && order.payment_status === "paid" && (
    !isCardOnDelivery || !!order.payment_gateway || order.status === "delivered"
  );

  return (
    <>
      <div className={`bg-white rounded-xl border-2 overflow-hidden transition-all ${deliveryLate || pickupLate ? "border-red-400 shadow-lg shadow-red-100" : isDelivering ? "border-purple-400 shadow-lg shadow-purple-100" : isReady ? "border-emerald-400" : "border-gray-200 opacity-70"}`} data-testid={`order-card-${order.id?.slice(-6)}`}>
        {/* Header */}
        <div className={`px-4 py-3 flex items-center justify-between ${deliveryLate || pickupLate ? "bg-red-50" : isDelivering ? "bg-purple-50" : isReady ? "bg-emerald-50" : "bg-gray-50"}`}>
          <div className="flex items-center gap-2">
            <Package className={`w-4 h-4 ${deliveryLate || pickupLate ? "text-red-600" : isDelivering ? "text-purple-600" : "text-emerald-600"}`} />
            <span className="font-bold text-sm">#{order.id?.slice(-6).toUpperCase()}</span>
            {order.payment_method && (
              <Badge variant="outline" className="text-[10px] px-1.5 py-0 gap-1" data-testid={`payment-badge-${order.id?.slice(-6)}`}>
                {order.payment_method === "cash" ? <Banknote className="w-3 h-3" /> : <CreditCard className="w-3 h-3" />}
                {paymentLabel[order.payment_method] || order.payment_method}
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-2">
            {pickupLate && <Badge className="bg-red-100 text-red-700 flex items-center gap-1 text-[10px]" data-testid={`pickup-late-badge-${order.id?.slice(-6)}`}><AlertTriangle className="w-3 h-3" />Atrasado para recolher</Badge>}
            {deliveryLate && <Badge className="bg-red-100 text-red-700 flex items-center gap-1 text-[10px]" data-testid={`delivery-late-badge-${order.id?.slice(-6)}`}><AlertTriangle className="w-3 h-3" />Entrega atrasada</Badge>}
            {!pickupLate && !deliveryLate && (
              <Badge className={isDelivering ? "bg-purple-100 text-purple-700" : isReady ? "bg-emerald-100 text-emerald-700" : "bg-gray-100 text-gray-500"}>
                {statusLabel}
              </Badge>
            )}
            <span className={`text-xs flex items-center gap-0.5 ${urgencyColor}`}>
              <Timer className="w-3 h-3" />
              {timeAgo(order.created_at)}
            </span>
          </div>
        </div>
        {/* Operational timing strip */}
        {(isReady || isDelivering) && (
          <div className={`px-4 py-1.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs border-b ${deliveryLate || pickupLate ? "bg-red-50/60" : "bg-gray-50"}`}>
            {dispatchedTime && (
              <span className="flex items-center gap-1 text-gray-400">
                <Clock className="w-3 h-3" />Recebido às {dispatchedTime}
              </span>
            )}
            {isReady && dispatchMinutes != null && (
              <span className={`flex items-center gap-1 ${pickupLate ? "text-red-600 font-semibold" : "text-gray-500"}`}>
                <Clock className="w-3 h-3" />A aguardar: {dispatchMinutes}min
              </span>
            )}
            {isDelivering && pickupMinutes != null && (
              <span className={`flex items-center gap-1 ${deliveryLate ? "text-red-600 font-semibold" : "text-purple-600"}`}>
                <Clock className="w-3 h-3" />Em entrega: {pickupMinutes}min
              </span>
            )}
            <span className={`flex items-center gap-1 ml-auto ${gpsStale ? "text-orange-600 font-semibold" : "text-emerald-600"}`} data-testid={`gps-status-${order.id?.slice(-6)}`}>{gpsStale ? "⚠ GPS sem atualizar" : "📍 GPS ativo"}</span>
          </div>
        )}

        <div className="p-4 space-y-3">
          {/* Customer */}
          {order.customer_name && (
            <div className="flex items-center gap-2 text-sm">
              <div className="w-7 h-7 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
                <span className="text-xs font-bold text-gray-600">{order.customer_name.charAt(0).toUpperCase()}</span>
              </div>
              <span className="font-medium">{order.customer_name}</span>
              {order.customer_phone && (
                <a href={`tel:${order.customer_phone}`} className="ml-auto text-emerald-600" data-testid={`call-btn-${order.id?.slice(-6)}`}>
                  <Phone className="w-4 h-4" />
                </a>
              )}
            </div>
          )}

          {/* Address */}
          {addr && (
            <div className="flex items-start gap-2 text-sm bg-gray-50 rounded-lg p-2.5">
              <MapPin className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
              <span className="text-gray-700 flex-1 text-xs leading-relaxed">{addr}</span>
            </div>
          )}

          {/* Notes */}
          {order.notes && (
            <div className="flex items-start gap-2 text-sm bg-amber-50 rounded-lg p-2.5 border border-amber-200" data-testid={`notes-${order.id?.slice(-6)}`}>
              <StickyNote className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
              <span className="text-amber-800 flex-1 text-xs leading-relaxed">{order.notes}</span>
            </div>
          )}

          {/* PAYMENT ALERT */}
          <div className={`rounded-lg p-3 border-2 ${
            isPaid
              ? "bg-green-50 border-green-400"
              : order.payment_status === "awaiting_payment" && !isCardOnDelivery
              ? "bg-amber-50 border-amber-400"
              : "bg-red-50 border-red-400 animate-pulse"
          }`} data-testid={`payment-alert-${order.id?.slice(-6)}`}>
            {isPaid ? (
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-green-600" />
                <span className="font-bold text-green-700 text-sm">{t("paymentPaid")}</span>
              </div>
            ) : order.payment_status === "awaiting_payment" && !isCardOnDelivery ? (
              <div className="flex items-center gap-2" data-testid={`payment-pending-${order.id?.slice(-6)}`}>
                <Timer className="w-5 h-5 text-amber-600" />
                <span className="font-bold text-amber-700 text-sm">{t("paymentPending")}</span>
              </div>
            ) : isCardOnDelivery ? (
              <div className="flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-red-600" />
                <div>
                  <span className="font-bold text-red-700 text-sm">{t("paymentCardOnDelivery")}</span>
                  <div className="text-red-600 font-bold text-lg">{(order.total || 0).toFixed(2)}€</div>
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <Banknote className="w-5 h-5 text-red-600" />
                <div>
                  <span className="font-bold text-red-700 text-sm">{t("paymentCashOnDelivery")}</span>
                  <div className="text-red-600 font-bold text-lg">{(order.total || 0).toFixed(2)}€</div>
                  {order.change_amount && order.change_amount > 0 && (
                    <div className="text-orange-600 text-xs font-medium mt-1">
                      ⚠️ {t("needsChange")}: {t("changeAmount")} {order.change_amount.toFixed(2)}€
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Items */}
          <div className="text-xs text-gray-500 space-y-0.5">
            {(order.items || []).slice(0, 4).map((item, i) => (
              <div key={i} className="flex justify-between">
                <span>{item.quantity}x {item.product_name}</span>
                <span className="text-gray-400">{(item.quantity * item.unit_price).toFixed(2)}</span>
              </div>
            ))}
            {(order.items || []).length > 4 && <p className="text-gray-400">+{order.items.length - 4} {t("moreItems")}</p>}
          </div>

          {/* Total — driver needs this to collect payment */}
          <div className="flex justify-between items-center pt-2 border-t">
            <span className="text-sm font-bold text-gray-900">Total</span>
            <span className="text-lg font-bold text-emerald-700">{(order.total || 0).toFixed(2)}</span>
          </div>

          {/* Delivery Photo */}
          {order.delivery_photo && (
            <div className="text-xs text-emerald-600 flex items-center gap-1">
              <Camera className="w-3 h-3" /> {t("photoRegistered")}
            </div>
          )}

          {/* Navigation buttons */}
          {addr && (isDelivering || isReady) && (
            <div className="grid grid-cols-2 gap-2">
              <Button variant="outline" size="sm" className="text-xs gap-1.5 border-blue-200 text-blue-600" onClick={openMaps} data-testid={`maps-btn-${order.id?.slice(-6)}`}>
                <Map className="w-3.5 h-3.5" /> Google Maps
              </Button>
              <Button variant="outline" size="sm" className="text-xs gap-1.5 border-cyan-200 text-cyan-600" onClick={openWaze} data-testid={`waze-btn-${order.id?.slice(-6)}`}>
                <Navigation className="w-3.5 h-3.5" /> Waze
              </Button>
            </div>
          )}

          {/* Action buttons */}
          {isReady && (
            <Button
              className="w-full h-11 bg-emerald-600 hover:bg-emerald-700 font-bold text-sm gap-2"
              onClick={() => onPickup(order.id)}
              disabled={loading}
              data-testid={`pickup-btn-${order.id?.slice(-6)}`}
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Truck className="w-4 h-4" /> {t("pickupBtn")}</>}
            </Button>
          )}
          {isDelivering && !hasArrived && (
            <div className="space-y-2">
              <Button
                className="w-full h-11 bg-amber-500 hover:bg-amber-600 font-bold text-sm gap-2"
                onClick={() => onArrived(order.id)}
                disabled={loading}
                data-testid={`arrived-btn-${order.id?.slice(-6)}`}
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><MapPin className="w-4 h-4" /> {t("arrivedBtn")}</>}
              </Button>
              <p className="text-[10px] text-gray-400 text-center">{t("arrivedHint")}</p>
            </div>
          )}
          {isDelivering && hasArrived && (
            <ArrivedCountdown arrivedAt={order.arrived_at} phone={order.customer_phone} />
          )}
          {isDelivering && hasArrived && (
            <div className="grid grid-cols-5 gap-2">
              <Button
                className="col-span-4 h-11 bg-purple-600 hover:bg-purple-700 font-bold text-sm gap-2"
                onClick={() => onDeliver(order.id)}
                disabled={loading}
                data-testid={`deliver-btn-${order.id?.slice(-6)}`}
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><CheckCircle2 className="w-4 h-4" /> {t("deliveredBtn")}</>}
              </Button>
              <Button
                variant="outline"
                className="h-11 border-gray-300"
                onClick={() => setShowPhoto(true)}
                data-testid={`photo-btn-${order.id?.slice(-6)}`}
              >
                <Camera className="w-4 h-4" />
              </Button>
            </div>
          )}
        </div>
      </div>

      {showPhoto && (
        <DeliveryPhotoModal
          orderId={order.id}
          token={token}
          onClose={() => setShowPhoto(false)}
          onSuccess={() => { setShowPhoto(false); toast.success(t("photoSaved")); }}
          t={t}
        />
      )}
    </>
  );
}
